import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, AlertTriangle, CircleCheck, CircleAlert, type LucideIcon } from 'lucide-react';
import { marketplaceColors } from '@aslen/ui-tokens';
import { toAppError } from '@aslen/core/api';
import { cn } from '@/lib/cn';
import { Button } from './button';

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

const toneClass: Record<Tone, string> = {
  neutral: 'bg-surface-muted text-text-secondary border-border',
  success: 'bg-success-tint text-success border-success/30',
  warning: 'bg-warning-tint text-warning border-warning/30',
  danger: 'bg-danger-tint text-danger border-danger/30',
  info: 'bg-info-tint text-info border-info/30',
  accent: 'bg-accent-tint text-text-primary border-accent',
};

/** Status chip — pill, tint background, full role colour text, 1px 30% border. */
export function StatusChip({ tone = 'neutral', children, className, dot }: { tone?: Tone; children: ReactNode; className?: string; dot?: boolean }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill border px-2 py-px text-[11px] font-semibold', toneClass[tone], className)}>
      {dot && <span className="size-1.5 rounded-pill bg-current" aria-hidden />}
      {children}
    </span>
  );
}

/**
 * Filter chip (Tümü · Müşteri · …). `size="md"` and `radio` make it the W-07
 * reason chip: one of a set rather than a toggle, so it announces itself as a
 * radio and the group around it carries the label.
 */
export function FilterChip({
  active,
  onClick,
  children,
  size = 'sm',
  radio,
  disabled,
  title,
  className,
}: {
  active?: boolean;
  onClick?(): void;
  children: ReactNode;
  size?: 'sm' | 'md';
  radio?: boolean;
  disabled?: boolean;
  title?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      role={radio ? 'radio' : undefined}
      aria-checked={radio ? !!active : undefined}
      aria-pressed={radio ? undefined : active}
      className={cn(
        'rounded-pill border transition-colors duration-150 disabled:cursor-not-allowed disabled:text-text-disabled',
        size === 'md' ? 'px-3 py-1.5 text-[12px]' : 'px-3 py-1 text-[12px]',
        active ? 'border-accent bg-accent-tint font-semibold text-text-primary' : 'border-border bg-surface text-text-secondary hover:bg-surface-muted',
        className,
      )}
    >
      {children}
    </button>
  );
}

/** "tahmin" — dashed, never filled. */
export function EstimateBadge({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn('inline-flex rounded-pill border border-dashed border-text-tertiary px-1.5 text-[10px] leading-4 text-text-secondary', className)}>
      {t('common.estimate')}
    </span>
  );
}

export function CountBadge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'danger' }) {
  return (
    <span
      className={cn(
        'inline-flex min-w-[17px] items-center justify-center rounded-pill px-1.5 text-[11px] font-semibold',
        tone === 'danger' ? 'h-[17px] bg-danger text-[10px] font-bold text-white' : 'bg-surface-muted py-px text-text-secondary',
      )}
    >
      {children}
    </span>
  );
}

export function LastUpdated({ children }: { children: ReactNode }) {
  return <span className="text-[12px] text-text-tertiary">{children}</span>;
}

/** Page panel with 1px border (structure by border, never shadow). */
export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <section className={cn('overflow-hidden rounded-card border border-border bg-surface', className)}>{children}</section>;
}

export function CardHeader({ title, aside, className, children }: { title: ReactNode; aside?: ReactNode; className?: string; children?: ReactNode }) {
  return (
    <div className={cn('flex items-center gap-2 border-b border-border px-4 py-3', className)}>
      <h2 className="text-[13px] font-semibold text-text-primary">{title}</h2>
      {children}
      {aside && <div className="ml-auto flex items-center gap-2">{aside}</div>}
    </div>
  );
}

export function KpiTile({
  label,
  value,
  unit,
  delta,
  note,
  estimate,
  tone,
  children,
  className,
  size = 'md',
  title,
}: {
  label: ReactNode;
  value?: ReactNode;
  unit?: ReactNode;
  delta?: ReactNode;
  note?: ReactNode;
  estimate?: boolean;
  tone?: 'danger' | 'warning' | 'success';
  children?: ReactNode;
  className?: string;
  /** `sm` is the W-09 stat tile: 20px value, 11px unit, 13/15 padding. */
  size?: 'sm' | 'md';
  /** Tooltip for a value that needs a reason (W-09: "Koli birimi olmayan 2 satır dahil değil"). */
  title?: string;
}) {
  return (
    <div
      title={title}
      className={cn(
        'rounded-card border bg-surface',
        size === 'sm' ? 'px-[15px] py-[13px]' : 'px-4 py-3.5',
        estimate ? 'border-dashed border-text-tertiary' : tone === 'danger' ? 'border-danger/35' : 'border-border',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-[12px] text-text-secondary">{label}</span>
        {estimate && <EstimateBadge />}
      </div>
      {value !== undefined && (
        <div className={cn('flex items-baseline gap-1.5', size === 'sm' ? 'mt-1.5' : 'mt-2')}>
          <span
            className={cn(
              'leading-tight font-semibold tracking-[-0.01em] tabular-nums',
              size === 'sm' ? 'text-[20px]' : 'text-[24px]',
              // the `-text` tokens are the same hue in light and a readable one in
              // dark, where plain `danger` on `surface` fails AA (§0.1)
              tone === 'danger' ? 'text-danger-text' : tone === 'warning' ? 'text-warning-text' : tone === 'success' ? 'text-success-text' : 'text-text-primary',
            )}
          >
            {value}
          </span>
          {unit && <span className={cn(size === 'sm' ? 'text-[11px] text-text-tertiary' : 'text-[14px] text-text-secondary')}>{unit}</span>}
          {delta && <span className="ml-auto text-[12px] font-semibold">{delta}</span>}
        </div>
      )}
      {children}
      {note && <div className="mt-2 text-[11px] text-text-tertiary">{note}</div>}
    </div>
  );
}

/** Empty state: dashed box → 34px icon → one sentence → one primary action. Never an illustration. */
export function EmptyState({ icon: Icon, children, action, className }: { icon: LucideIcon; children: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 rounded-card border border-dashed border-border-strong px-6 py-10 text-center', className)}>
      <Icon className="size-[34px] text-text-tertiary" strokeWidth={1.5} aria-hidden />
      <p className="max-w-md text-[13px] text-text-secondary">{children}</p>
      {action}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton h-3', className)} aria-hidden />;
}

export function SkeletonRows({ rows = 6, widths = ['100%', '72%', '48%'] }: { rows?: number; widths?: string[] }) {
  return (
    <div className="flex flex-col gap-3 p-4" aria-busy>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton h-3" style={{ width: widths[i % widths.length] }} />
      ))}
    </div>
  );
}

const bannerIcon: Record<'danger' | 'warning' | 'info' | 'success', LucideIcon> = {
  danger: CircleAlert,
  warning: AlertTriangle,
  info: Info,
  success: CircleCheck,
};

/** Alert band: tint + border, round icon, title 13/600 + body 12, one action on the right. */
export function AlertBanner({
  tone,
  title,
  children,
  action,
  className,
}: {
  tone: 'danger' | 'warning' | 'info' | 'success';
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const Icon = bannerIcon[tone];
  const tint = { danger: 'bg-danger-tint border-danger/30', warning: 'bg-warning-tint border-warning/30', info: 'bg-info-tint border-info/30', success: 'bg-success-tint border-success/30' }[tone];
  const fg = { danger: 'text-danger', warning: 'text-warning', info: 'text-info', success: 'text-success' }[tone];
  return (
    <div role={tone === 'danger' ? 'alert' : 'status'} className={cn('flex items-center gap-3 rounded-card border px-4 py-3', tint, className)}>
      <Icon className={cn('size-5 flex-none', fg)} strokeWidth={1.5} aria-hidden />
      <div className="min-w-0 flex-1">
        {title && <div className="text-[13px] font-semibold text-text-primary">{title}</div>}
        {children && <div className="text-[12px] leading-relaxed text-text-secondary">{children}</div>}
      </div>
      {action}
    </div>
  );
}

/**
 * W-32 "Veri yüklenemedi": what a failed query shows instead of the empty state,
 * so a server that cannot be reached never reads as "no records yet". The
 * technical line is kept small underneath — it is what the operator forwards.
 */
export function ErrorState({ error, onRetry, className }: { error?: unknown; onRetry?(): void; className?: string }) {
  const { t } = useTranslation();
  const detail = error ? toAppError(error).message : null;
  return (
    <AlertBanner
      tone="danger"
      title={t('states.error')}
      className={className}
      action={
        onRetry && (
          <Button size="sm" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        )
      }
    >
      {t('states.errorBody')}
      {detail && <div className="mt-1 font-mono text-[11px] break-words text-text-tertiary">{detail}</div>}
    </AlertBanner>
  );
}

/** Key–value list (künye). */
export function KeyValueList({ rows }: { rows: { k: ReactNode; v: ReactNode }[] }) {
  return (
    <dl className="flex flex-col">
      {rows.map((r, i) => (
        <div key={i} className="flex justify-between gap-3 border-b border-hairline py-1">
          <dt className="text-[12px] text-text-secondary">{r.k}</dt>
          <dd className="text-right text-[12px] font-medium text-text-primary">{r.v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Avatar({ initials, color, size = 28, title }: { initials: string; color?: string | null; size?: number; title?: string }) {
  return (
    <span
      title={title}
      className="inline-flex flex-none items-center justify-center rounded-pill font-semibold text-white"
      style={{ width: size, height: size, background: color ?? '#6B6B62', fontSize: Math.max(9, Math.round(size * 0.38)) }}
    >
      {initials}
    </span>
  );
}

export function ContactAvatar({ name, type, size = 24 }: { name: string; type: string; size?: number }) {
  const parts = name.trim().split(/\s+/);
  const ini = (parts.length > 1 ? parts[0]!.charAt(0) + parts[1]!.charAt(0) : name.slice(0, 2)).toLocaleUpperCase('tr-TR');
  const cls =
    type === 'customer' || type === 'both'
      ? 'bg-info-tint text-info'
      : type === 'supplier'
        ? 'bg-warning-tint text-warning'
        : type === 'partner'
          ? 'bg-accent-tint text-text-primary'
          : 'bg-surface-muted text-text-secondary';
  return (
    <span className={cn('inline-flex flex-none items-center justify-center rounded-[5px] font-bold', cls)} style={{ width: size, height: size, fontSize: size > 30 ? 12 : 9 }}>
      {ini}
    </span>
  );
}

export function MarketplaceBadge({ code }: { code: string }) {
  const color = (marketplaceColors as Record<string, string>)[code] ?? '#6B6B62';
  return (
    <span className="rounded-[3px] px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ background: color }}>
      {code}
    </span>
  );
}

export function ColorChip({ color, size = 12 }: { color: string; size?: number }) {
  return <span className="inline-block flex-none rounded-pill border border-[rgba(31,31,29,.25)]" style={{ width: size, height: size, background: color }} aria-hidden />;
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-3.5 flex flex-wrap items-end gap-3.5">
      <div className="min-w-0">
        <h1 className="text-[24px] leading-tight font-semibold tracking-[-0.01em] text-text-primary">{title}</h1>
        {subtitle && <div className="mt-0.5 text-[13px] text-text-secondary">{subtitle}</div>}
      </div>
      {actions && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Underline tabs (W-10b style). */
export function UnderlineTabs<T extends string>({ tabs, value, onChange }: { tabs: { value: T; label: ReactNode }[]; value: T; onChange(v: T): void }) {
  return (
    <div role="tablist" className="flex gap-0.5 border-b border-border">
      {tabs.map((t) => (
        <button
          key={t.value}
          role="tab"
          type="button"
          aria-selected={t.value === value}
          onClick={() => onChange(t.value)}
          className={cn(
            '-mb-px border-b-2 px-4 py-2 text-[13px] transition-colors duration-150',
            t.value === value ? 'border-accent font-semibold text-text-primary' : 'border-transparent text-text-secondary hover:text-text-primary',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
